import { api } from "./api";

const urlB64ToUint8 = (base64) => {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export const pushSupported = () =>
  "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

export async function getPushSubscription() {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

// Re-post an existing browser subscription once per session so the server knows its
// user-agent (mobile browsers are skipped when the member also has the native app).
let synced = false;
export async function syncPushSubscription() {
  if (synced) return;
  synced = true;
  try {
    const sub = await getPushSubscription();
    if (sub) await api.post("/push/subscribe", { subscription: sub.toJSON() });
  } catch { /* noop */ }
}

export async function enablePush() {
  if (!pushSupported()) throw new Error("unsupported");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("denied");
  const reg = await navigator.serviceWorker.ready;
  const { data } = await api.get("/push/public-key");
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlB64ToUint8(data.public_key),
  });
  await api.post("/push/subscribe", { subscription: sub.toJSON() });
  return sub;
}

// Logout (web/PWA): dissociate THIS browser's subscription from the account on the
// server, so the still-active service worker stops receiving the member's private
// notifications. The browser subscription itself is kept (permission stays granted):
// the next login on this browser re-attaches it to that account via syncPushSubscription.
export async function detachPushSubscription() {
  try {
    const sub = await getPushSubscription();
    if (!sub) return false;
    await api.post("/push/unsubscribe", { subscription: sub.toJSON() });
    synced = false;
    return true;
  } catch {
    return false;
  }
}

// No session in this browser (signed out, expired, or logged out before the logout fix
// shipped): make sure its subscription is bound to NO account. Public endpoint, fire-and-forget.
let detachedAnon = false;
export async function detachOrphanPushSubscription() {
  if (detachedAnon) return;
  detachedAnon = true;
  try {
    const sub = await getPushSubscription();
    if (sub) await api.post("/push/detach", { endpoint: sub.endpoint });
  } catch { /* noop */ }
}

export async function disablePush() {
  const sub = await getPushSubscription();
  if (sub) {
    await api.post("/push/unsubscribe", { subscription: sub.toJSON() }).catch(() => {});
    await sub.unsubscribe();
  }
}
